import { Suspense, useCallback, useMemo } from 'react';
import { TwoFactorTargetConfigMapping } from '@cbhq/identity-shared/config';
import { useBaseUrlMiddleware } from '@cbhq/identity-shared/hooks';
import { BaseIntlProvider } from '@cbhq/identity-shared/web';
import { FetchableResourceProvider } from '@cbhq/instant-api-hooks-core/fetch';
import { getClientLocale } from '@cbhq/intl';

import { AppSwitcherButton } from './components/AppSwitcherButton';
import { AppSwitcherContent } from './components/AppSwitcherContent';
import { AppSwitcherContentFallback } from './components/AppSwitcherContentFallback';
import { AppSwitcherInside } from './components/AppSwitcherInside';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useMockMiddleware } from './external/middleware';
import { getFetchClient } from './external/utils/getFetchClient';
import { mockGetApplications } from './mocks/mockGetApplications';
import { PrimeModalProvider } from './module/PrimeModalProvider';
import { ApplicationsProvider } from './providers/ApplicationsProvider';
import { ClientPropsProvider } from './providers/ClientPropsProvider';
import type { AppSwitcherProps } from './AppSwitcher.types';

const handlers = {
  ['GetApplicationsV2'.toLocaleLowerCase()]: mockGetApplications,
};

const OVERRIDE_ENABLED_HOSTS = [
  // coinbase.com
  '2ZPgNhLdLmW6rbxN5CiAYU98pOd',
];

type WrapperProps = {
  children: React.ReactElement;
  fallback: React.ReactElement;
} & AppSwitcherProps;

function Wrapper(props: WrapperProps) {
  const locale = props?.config?.locale ?? getClientLocale();

  const authedFetch = getFetchClient();
  const mockFetch = useMockMiddleware(
    handlers,
    '/coinbase.app_picker.AppPickerService/',
    props.projectId,
  );

  const urlOverride = useCallback(
    () =>
      OVERRIDE_ENABLED_HOSTS.includes(props.projectId)
        ? TwoFactorTargetConfigMapping[props.target].retailHost
        : undefined,
    [props],
  );

  const baseUrlMiddleware = useBaseUrlMiddleware(props.target, urlOverride);

  const baseFetch = useMemo(
    () => (props.target === 'mock' ? mockFetch() : authedFetch),
    [authedFetch, mockFetch, props.target],
  );

  const fetchWithMiddleware = baseUrlMiddleware(baseFetch);

  return (
    <BaseIntlProvider
      isProd={props.target === 'production'}
      locale={locale}
      awaitLoadMessages
      skipFetchMessages={props.skipFetchMessages}
    >
      <ClientPropsProvider appSwitcherProps={props}>
        <ErrorBoundary>
          <FetchableResourceProvider fetch={fetchWithMiddleware}>
            <Suspense fallback={props.fallback}>
              <ApplicationsProvider>
                <PrimeModalProvider>{props.children}</PrimeModalProvider>
              </ApplicationsProvider>
            </Suspense>
          </FetchableResourceProvider>
        </ErrorBoundary>
      </ClientPropsProvider>
    </BaseIntlProvider>
  );
}

export function AppSwitcher(props: AppSwitcherProps) {
  return (
    <Wrapper {...props} fallback={<AppSwitcherButton />}>
      <AppSwitcherContent />
    </Wrapper>
  );
}

// Used by mfe-app-switcher to share the app switcher content dynamic
export function AppSwitcherView(props: AppSwitcherProps) {
  return (
    <Wrapper {...props} fallback={<AppSwitcherContentFallback />}>
      <AppSwitcherInside />
    </Wrapper>
  );
}
